import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import Button from 'react/components/Button';
import TextInput from 'react/elements/TextInput';
import MaterialIcon from 'react/elements/MaterialIcon';
import { ideasOf } from 'react/components/calendar/mealIdeasOfItem.js';
import GroceryItemDetails from './GroceryItemDetails.jsx';
import ItemKinds from './ItemKinds.jsx';
import IngredientInIdea from './IngredientInIdea.jsx';
import RowOnList from './RowOnList.jsx';
import ItemAsMealIdea from './ItemAsMealIdea.jsx';
import { isWebAddress } from 'react/components/mealIdeas/RecipeCard';
import { setMealIdeaIngredients, MEAL_IDEAS_LOADED } from 'redux/actions/mealIdeas';
import { selectMealIdeasIndex } from '../../../redux/food/selectors';
import useOffline, { NEEDS_CONNECTION_TITLE, onlineOnly } from '../../hooks/useOffline';
import {
	fetchGroceryItem,
	lookUpGroceryItem,
	renameGroceryItem,
	splitGroceryListItem,
} from 'redux/actions/groceryItems';

const day = (ymd) => moment(ymd, 'YYYY-MM-DD').format('MMM D, YYYY');

/** "Tacos", "Tacos and Soup", "Tacos, Soup and Chili". */
export function joinNames(names) {
	if(names.length < 2) {
		return names.join('');
	}
	return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * A meal idea's name. Given `onOpen` it opens the idea, with a separate link to its recipe; without,
 * the name itself links to the recipe. Either recipe link is only for one at a web address.
 */
function IdeaName({ idea, name, onOpen = null }) {
	const url = idea && isWebAddress(idea.recipe_url) ? idea.recipe_url : null;
	if(onOpen && idea) {
		return (
			<span className='grocery_item_editor__idea'>
				<a
					href='#'
					className='grocery_item_editor__idea-open'
					onClick={(event) => {
						event.preventDefault();
						onOpen(idea.id);
					}}
				>
					{name}
				</a>
				{!!url &&
				<a
					href={url}
					className='grocery_item_editor__recipe'
					target='_blank'
					rel='noopener noreferrer'
					title={`The recipe for ${name}`}
					aria-label={`The recipe for ${name}`}
				>
					<MaterialIcon>open_in_new</MaterialIcon>
				</a>
				}
			</span>
		);
	}
	if(!url) {
		return name;
	}
	return (
		<a href={url} target='_blank' rel='noopener noreferrer' title={`The recipe for ${name}`}>{name}</a>
	);
}

/**
 * One grocery list item, opened from the list it is on: what the item is used in, when it was
 * bought, its tags, what it is a kind of or the kinds it has (ItemKinds), its details
 * (GroceryItemDetails), and renaming it. The item is the budget's one of
 * that name (models/grocery_item.php), so what it shows is about every list and every meal idea, not
 * only this row.
 *
 * `row` is the GroceryListItem tapped, whose own amount and notes are set here (RowOnList).
 * `onRemove`, given only for a row still on the active list, takes it off. A row still on the active list can be renamed two ways: everywhere, which renames
 * the item in every meal idea (and merges it into an item that already has the new name), or on its
 * own, which takes only this row and the ideas it is on the list for to the new name. A bought row
 * is a record of the shop, so it offers only the first, and keeps its own name through it.
 *
 * Opened from a meal idea's ingredient instead, it is given `idea` and `ingredientId` rather than
 * `row`. It then also sets how much of the item that idea takes (IngredientInIdea), and the second
 * rename is "Only in" the idea: the ingredient alone moves to the new name. Closing goes back to the
 * idea, so the close button is a back arrow. `backTo`, naming what it goes back to, makes it one too
 * for a row opened in place of a list rather than over it.
 *
 * A meal idea named in its lists links to the idea's recipe, when it has one at a web address.
 * Given `onOpenIdea`, which takes the idea's id, the name opens the idea instead, and the recipe link
 * moves to an icon beside it.
 *
 * Unless it was opened from an ingredient, an item that was really a meal can be made a meal idea
 * here (ItemAsMealIdea), and `onOpenIdea` then opens that idea.
 *
 * Given `item` ({ id, name }) rather than a row or an ingredient, it is that item on its own, as
 * opened from another item's kinds: it renames only everywhere. `onOpenItem`, which takes an item's
 * id and name, makes the names under Kinds open their items.
 *
 * Renaming and removing from the list are online-only, as is every save its sections make but the
 * row's amount and notes, which are an op. Offline the online-only ones are disabled, titled "Needs a
 * connection", and the sections' text fields read-only.
 */
export default function GroceryItemEditor({ row = null, idea = null, ingredientId = null, item: bare = null, onClose, onRemove = null, backTo = null, onOpenIdea = null, onOpenItem = null }) {
	const dispatch = useDispatch();
	const ideasById = useSelector((state) => selectMealIdeasIndex(state).ideasById);
	const [details, setDetails] = useState(null);
	const [error, setError] = useState(null);
	const [draft, setDraft] = useState(null);
	const [choosing, setChoosing] = useState(null);
	const [saving, setSaving] = useState(false);
	const mounted = useRef(true);
	const offline = useOffline();
	const ingredients = idea ? idea.ingredients || [] : [];
	const ingredient = idea ? ingredients.find((each) => each.id === ingredientId) || null : null;
	const itemId = row ? row.grocery_item_id : ingredient ? ingredient.grocery_item_id : bare && bare.id;
	const canSplit = row ? !!onRemove : !!ingredient;
	const back = idea ? idea.name : backTo;

	// Set on mount as well as cleared on unmount: a hot reload runs the cleanup and then mounts again.
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	// For a generic, the kinds bought on a day, after its date.
	const boughtAs = (ymd) => {
		const kinds = details && details.bought_kinds ? details.bought_kinds[ymd] : null;
		return kinds && kinds.length ? <span className='grocery_item_editor__muted'> · {kinds.join(', ')}</span> : null;
	};

	// What a section of the editor saved, or why it was refused.
	const saved = (answer) => {
		if(mounted.current) {
			setDetails(answer);
			setError(null);
		}
	};
	const failed = (message) => mounted.current && setError(message);

	const settle = (promise) => promise
		.then((answer) => {
			if(mounted.current) {
				setDetails(answer);
				setError(null);
			}
		})
		.catch(() => {
			if(mounted.current) {
				setError('That did not load. Try again.');
			}
		});

	// An ingredient renamed on its own moves to another item, and the editor follows it there.
	useEffect(() => {
		if(itemId) {
			settle(fetchGroceryItem(itemId));
		}
	}, [itemId]);

	const fallbackName = row ? row.name : ingredient ? ingredient.name : bare ? bare.name : '';
	const item = details ? details.item : { id: itemId, name: fallbackName };
	// The ideas this rename on its own takes along: the row's, or the one idea the ingredient is in.
	const rowIdeas = row ? ideasOf(row, ideasById) : idea ? [idea] : [];
	const usedIn = details ? details.meal_ideas : [];

	const startRename = () => {
		setDraft(item.name);
		setChoosing(null);
	};

	const cancelRename = () => {
		setDraft(null);
		setChoosing(null);
	};

	/** The name is checked against the budget's items, so the choice can say if it would merge. */
	const proposeRename = () => {
		const name = (draft || '').trim();
		if(!name || name === item.name) {
			cancelRename();
			return;
		}
		setSaving(true);
		lookUpGroceryItem(name)
			.then((existing) => {
				if(mounted.current) {
					setChoosing({ name, existing: existing && existing.id !== item.id ? existing : null });
				}
			})
			.catch(() => mounted.current && setError('That name could not be checked. Try again.'))
			.finally(() => mounted.current && setSaving(false));
	};

	/**
	 * Renamed in the idea alone, the ingredient points at the new name's item, which is then shown.
	 * A refused save resolves rather than rejects, so it is turned into a rejection here.
	 */
	const renameIngredient = (name) => dispatch(setMealIdeaIngredients(idea.id, (list) => list.map((each) =>
		(each.id === ingredientId ? { ...each, name } : each)))).then((action) => {
		if(!action || action.type !== MEAL_IDEAS_LOADED) {
			return Promise.reject(action);
		}
		return null;
	});

	const rename = (everywhere) => {
		setSaving(true);
		const request = everywhere
			? dispatch(renameGroceryItem(item.id, choosing.name))
			: row ? dispatch(splitGroceryListItem(row.id, choosing.name)) : renameIngredient(choosing.name);
		request.then((answer) => {
			if(answer && mounted.current) {
				setDetails(answer);
				setError(null);
			}
		}).catch(() => mounted.current && setError('That did not save. Try again.')).finally(() => {
			if(mounted.current) {
				setSaving(false);
				cancelRename();
			}
		});
	};

	const otherIdeas = usedIn.filter((used) => !rowIdeas.some((rowIdea) => rowIdea.id === used.id));

	return (
		<div className='meal_idea_panel grocery_item_editor'>
			<div className='panel_heading'>
				{draft === null &&
				<h5 className='meal_idea_details__name'>{item.name}</h5>
				}
				{draft !== null &&
				<div className='input-field meal_idea_panel__name'>
					<TextInput
						aria-label='New name'
						autoFocus
						value={draft}
						disabled={saving || !!choosing}
						onChange={(event) => setDraft(event.target.value)}
						onKeyDown={(event) => {
							if(event.key === 'Enter') {
								event.preventDefault();
								proposeRename();
							}
							if(event.key === 'Escape') {
								event.preventDefault();
								cancelRename();
							}
						}}
					/>
				</div>
				}
				{draft === null && !!item.id &&
				<button
					type='button'
					className='btn-flat meal_idea_panel__icon grocery_item_editor__rename'
					title={offline ? NEEDS_CONNECTION_TITLE : 'Rename'}
					aria-label='Rename'
					disabled={offline}
					onClick={startRename}
				>
					<MaterialIcon>edit</MaterialIcon>
				</button>
				}
				{back ?
				<button type='button' className='btn-flat meal_idea_panel__icon' title={`Back to ${back}`} aria-label={`Back to ${back}`} onClick={onClose}>
					<MaterialIcon>arrow_back</MaterialIcon>
				</button> :
				<button type='button' className='btn-flat meal_idea_panel__icon' title='Close' aria-label='Close' onClick={onClose}>
					<MaterialIcon>close</MaterialIcon>
				</button>
				}
			</div>

			{draft !== null && !choosing &&
			<div className='meal_idea_panel__confirm grocery_item_editor__renaming'>
				<Button flat onClick={cancelRename}>Cancel</Button>
				<Button flat className='grocery_item_editor__next' disabled={saving} {...onlineOnly(offline)} onClick={proposeRename}>
					{saving ? 'Checking...' : 'Rename'}
				</Button>
			</div>
			}

			{!!choosing &&
			<div className='grocery_item_editor__choices' role='group' aria-label={`Rename ${item.name} to ${choosing.name}`}>
				<button type='button' className='btn-flat grocery_item_editor__choice is-everywhere' disabled={saving} {...onlineOnly(offline)} onClick={() => rename(true)}>
					<strong>Rename everywhere</strong>
					<span>
						{usedIn.length
							? `${joinNames(usedIn.map((used) => used.name))} will use “${choosing.name}”, and so will the grocery list.`
							: `“${item.name}” becomes “${choosing.name}” on the grocery list.`}
						{' '}Lists already bought keep the name they were bought by.
					</span>
					{!!choosing.existing &&
					<span className='grocery_item_editor__merge'>
						{`“${choosing.existing.name}” already exists, so the two become one item.`}
					</span>
					}
				</button>
				{canSplit &&
				<button type='button' className='btn-flat grocery_item_editor__choice is-split' disabled={saving} {...onlineOnly(offline)} onClick={() => rename(false)}>
					<strong>{row ? 'Only this one' : `Only in ${idea.name}`}</strong>
					<span>
						{!row
							? `${idea.name} uses “${choosing.name}”.`
							: rowIdeas.length
								? `Here and in ${joinNames(rowIdeas.map((each) => each.name))} it becomes “${choosing.name}”.`
								: `Only here it becomes “${choosing.name}”.`}
						{otherIdeas.length > 0 && ` ${joinNames(otherIdeas.map((each) => each.name))} keep${otherIdeas.length === 1 ? 's' : ''} “${item.name}”.`}
					</span>
					{!!choosing.existing &&
					<span className='grocery_item_editor__merge'>
						{row
							? `“${choosing.existing.name}” already exists, so this joins it.`
							: `“${choosing.existing.name}” already exists, so ${idea.name} uses that.`}
					</span>
					}
				</button>
				}
				<Button flat className='grocery_item_editor__back' disabled={saving} onClick={() => setChoosing(null)}>Back</Button>
			</div>
			}

			<div className='meal_idea_panel__body'>
				{!!error && <p className='meal_idea_panel__name-error'>{error}</p>}

				{!!details && !!details.item.image_url &&
				<img className='grocery_item_editor__image' src={details.item.image_url} alt={item.name} />
				}

				{!!ingredient &&
				<IngredientInIdea idea={idea} ingredient={ingredient} />
				}

				{!!row && !!row.id &&
				<RowOnList rowId={row.id} heading={canSplit ? 'On this list' : 'When bought'} />
				}

				{!!row && rowIdeas.length > 0 &&
				<section className='meal_idea_panel__section'>
					<h6>{canSplit ? 'On this list for' : 'Bought for'}</h6>
					<ul className='grocery_item_editor__list'>
						{(row.meal_ideas || []).filter((link) => ideasById[link.meal_idea_id]).map((link) =>
						<li key={`${link.meal_idea_id}-${link.meal_idea_ingredient_id}`}>
							<IdeaName idea={ideasById[link.meal_idea_id]} name={ideasById[link.meal_idea_id].name} onOpen={onOpenIdea} />
							{!!link.quantity && <span className='grocery_item_editor__quantity'>{link.quantity}</span>}
						</li>
						)}
					</ul>
				</section>
				}

				<section className='meal_idea_panel__section'>
					<h6>Used in</h6>
					{!details && !error && <p className='grocery_item_editor__muted'>Loading...</p>}
					{!!details && usedIn.length === 0 && <p className='grocery_item_editor__muted'>No meal ideas use it.</p>}
					{usedIn.length > 0 &&
					<ul className='grocery_item_editor__list'>
						{usedIn.map((used) =>
						<li key={used.id}>
							<IdeaName idea={ideasById[used.id]} name={used.name} onOpen={onOpenIdea} />
							{!!used.kind && <span className='grocery_item_editor__quantity'>{used.kind}</span>}
							{!!used.quantity && <span className='grocery_item_editor__quantity'>{used.quantity}</span>}
						</li>
						)}
					</ul>
					}
				</section>

				<section className='meal_idea_panel__section'>
					<h6>Bought</h6>
					{!!details && details.bought_count === 0 && <p className='grocery_item_editor__muted'>Never bought.</p>}
					{!!details && details.bought_count > 0 &&
					<React.Fragment>
						<p className='grocery_item_editor__last-bought'>
							Last bought {day(details.last_bought)}{boughtAs(details.last_bought)}
							{details.bought_count > 1 && <span className='grocery_item_editor__muted'> · {details.bought_count} times</span>}
						</p>
						{details.bought.length > 1 &&
						<ul className='grocery_item_editor__history'>
							{details.bought.slice(1).map((ymd) => <li key={ymd}>{day(ymd)}{boughtAs(ymd)}</li>)}
						</ul>
						}
					</React.Fragment>
					}
				</section>

				{!!details && !ingredient &&
				<ItemAsMealIdea
					details={details}
					onChanged={() => settle(fetchGroceryItem(item.id))}
					onConverted={onOpenIdea}
				/>
				}

				{!!details &&
				<GroceryItemDetails
					details={details}
					onSaved={saved}
					onError={failed}
					afterTags={<ItemKinds details={details} onSaved={saved} onError={failed} onOpenItem={onOpenItem} />}
				/>
				}
			</div>

			{!!onRemove &&
			<div className='meal_idea_panel__footer'>
				<div className='meal_idea_panel__actions'>
					<Button flat className='meal_idea_panel__remove' {...onlineOnly(offline)} onClick={onRemove}>
						<MaterialIcon className='left'>remove_shopping_cart</MaterialIcon>
						Remove from list
					</Button>
				</div>
			</div>
			}
		</div>
	);
}
